/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect, useCallback } from "react";
import { flushSync } from "react-dom";

import {
  Button,
  VStack,
  HStack,
  Text,
  Select,
  Box,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Progress,
  Spinner,
  Center,
  useToast,
} from "@chakra-ui/react";
import WaveSurfer from "wavesurfer.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record.esm.js";
import {
  initAudioRecordingsDB,
  AUDIO_RECORDINGS_DB_AUDIO_BLOB,
} from "../services/db";
import { AUDIO_RECORDINGS_DB_STORE } from "../services/db";
import { uploadAudio, MP4_MIME_TYPE } from "../services/audioUploader";
import { getSignatureUrl } from "../services/backend";
import { HiOutlineUpload } from "react-icons/hi";
import { AiOutlineAudio } from "react-icons/ai";
import { HiOutlineTrash } from "react-icons/hi";
import { HiOutlinePlay } from "react-icons/hi";
import { HiOutlineStop } from "react-icons/hi";
import { HiOutlinePause } from "react-icons/hi";

const AudioRecorder = ({
  onUploadComplete,
  uploadType,
  objectId,
  disableCreateButton,
  onLoadingChange,
}) => {
  const [recording, setRecording] = useState(false);
  // True when an unsaved, just-recorded clip is present (vs. existing/saved
  // signature audio). Drives the Record→Discard swap and the Upload highlight.
  const [hasNewRecording, setHasNewRecording] = useState(false);
  const [progress, setProgress] = useState("00:00");
  const [availableMics, setAvailableMics] = useState([]);
  const [selectedMic, setSelectedMic] = useState("");
  const [playbackView, setPlaybackView] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0); // For overall progress
  const [isUploading, setIsUploading] = useState(false); // To show/hide the modal
  // False while a clip is decoding; gates the Play control so the user can't
  // start a half-decoded clip (which would stall mid-playback).
  const [audioReady, setAudioReady] = useState(false);
  const waveformRef = useRef(null);
  const waveSurferRef = useRef(null);
  const recordPluginRef = useRef(null);
  const toast = useToast();
  const [isLoadingSignature, setIsLoadingSignature] = useState(false);
  // True until we've resolved whether existing audio exists, so we don't flash
  // the playback ("recorded") controls before the record controls (or vice versa).
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    // Populate the mic picker (requests permission so device labels resolve).
    // Fire-and-forget: a pending permission prompt must not block the UI.
    loadAvailableMics();
    const boot = async () => {
      try {
        if (uploadType === "conversation") {
          await loadStoredAudio();
        } else {
          await loadSignatureAudio();
        }
      } finally {
        setInitializing(false);
      }
    };
    boot();
  }, [uploadType, objectId]);

  const initializeWaveSurfer = useCallback(() => {
    if (waveSurferRef.current) {
      try {
        waveSurferRef.current.abortController = undefined;
        waveSurferRef.current.destroy();
      } catch (error) {
        console.error("Error destroying WaveSurfer instance:", error);
      }
    }

    waveSurferRef.current = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "rgb(200, 0, 200)",
      progressColor: "rgb(100, 0, 100)",
      scrollParent: true,
      // Use the real <audio> element (MediaElement) so we can gate playback on
      // its readyState. WaveSurfer's own "ready" event only means the WAVEFORM
      // finished decoding; the player buffers SEPARATELY and lazily, so "ready"
      // fires instantly while the first play still stalls. We instead wait for
      // the media element's "canplaythrough" (HAVE_ENOUGH_DATA = can play to the
      // end without re-buffering) before enabling Play — see loadWaveSurferWithAudio.
      backend: "MediaElement",
      height: 100,
    });

    recordPluginRef.current = waveSurferRef.current.registerPlugin(
      RecordPlugin.create({
        mediaType: MP4_MIME_TYPE,
        audioBitsPerSecond: 128000, // Set bitrate if necessary
        scrollingWaveform: true,
        renderRecordedAudio: false,
      })
    );

    recordPluginRef.current.on("record-end", async (blob) => {
      // Save the raw recording as-is — no post-capture conversion/processing.
      const recordedUrl = URL.createObjectURL(blob);
      await storeRecording(blob);
      // Recording left the session in "play-and-record"; restore "playback" NOW
      // (well before the user clicks Play, so the audio route settles) rather
      // than at play time, where a route switch would glitch the first play.
      if (navigator.audioSession) {
        navigator.audioSession.type = "playback";
      }
      setHasNewRecording(true);
      setPlaybackView(true);
      loadWaveSurferWithAudio(recordedUrl);
    });

    recordPluginRef.current.on("record-progress", (time) => {
      const formattedTime = formatTime(time / 1000);
      setProgress(formattedTime);
    });

    waveSurferRef.current.on("ready", () => {
      setDuration(waveSurferRef.current.getDuration());
    });

    waveSurferRef.current.on("audioprocess", () => {
      setCurrentTime(waveSurferRef.current.getCurrentTime());
    });

    waveSurferRef.current.on("seek", () => {
      setCurrentTime(waveSurferRef.current.getCurrentTime());
    });
  }, []);

  const loadWaveSurferWithAudio = useCallback((audioUrl) => {
    // Gate the Play control on the PLAYBACK element being able to play through
    // without re-buffering (readyState HAVE_ENOUGH_DATA), NOT on WaveSurfer's
    // "ready" (which only means the waveform finished decoding — the player
    // buffers separately and lazily, which is what made the first play stall).
    setAudioReady(false);
    const ws = waveSurferRef.current;
    const media = ws.getMediaElement?.();

    let settled = false;
    const markReady = () => {
      if (settled) return;
      settled = true;
      setDuration(ws.getDuration());
      setAudioReady(true);
    };

    if (media) {
      // Attach before load() sets the new src so we never miss the event.
      media.addEventListener("canplaythrough", markReady, { once: true });
      // Safety net: don't trap the user behind a spinner forever if an engine
      // is stingy about canplaythrough for blob URLs — fall back to the weaker
      // "canplay" only after a grace period.
      const fallback = setTimeout(() => {
        if (!settled && media.readyState >= 3) markReady();
      }, 1500);
      const hardStop = setTimeout(markReady, 8000);
      const clear = () => {
        clearTimeout(fallback);
        clearTimeout(hardStop);
      };
      media.addEventListener("canplaythrough", clear, { once: true });
    } else {
      ws.once("ready", markReady);
    }

    const loaded = ws.load(audioUrl);
    if (loaded && typeof loaded.catch === "function") {
      loaded.catch((error) => {
        console.error("Error loading audio for playback:", error);
        markReady(); // never strand the user on a spinner after a load error
      });
    }
  }, []);

  const loadAvailableMics = useCallback(async () => {
    // Device labels are only exposed after mic permission is granted, so prompt
    // up front to populate the picker on every engine.
    try {
      // Safari needs the audio session in a capture-capable mode for
      // getUserMedia; Chrome/Firefox have no audioSession API (no-op there).
      if (navigator.audioSession) {
        navigator.audioSession.type = "play-and-record";
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      console.warn("Mic permission not granted up front:", error);
    } finally {
      // Leave the session in "playback" NOW (on mount, no audio playing) so the
      // route is settled well before the user hits Play. The first-play glitch
      // we fought came from the session being in capture mode AT play time or
      // toggled during playback — neither happens anymore (record-end also
      // resets to "playback", and we never toggle in handlePlayPause).
      if (navigator.audioSession) {
        navigator.audioSession.type = "playback";
      }
    }
    try {
      const devices = await RecordPlugin.getAvailableAudioDevices();
      setAvailableMics(devices);
    } catch (error) {
      console.error("Error enumerating audio devices:", error);
    }
  }, []);

  const handleStartStopRecording = () => {
    if (
      recordPluginRef.current.isRecording() ||
      recordPluginRef.current.isPaused()
    ) {
      recordPluginRef.current.stopRecording();
      setTimeout(() => {
        flushSync(() => {
          setRecording(false);
        });
      }, 1);
    } else {
      const deviceId = selectedMic || availableMics[0]?.deviceId;
      // Safari/WebKit: the live WaveSurfer audio context leaves the page's
      // audio session type at "auto", which resolves to a playback-only
      // category — so getUserMedia for the mic is rejected ("AudioSession
      // category is not compatible with audio capture") before it even
      // prompts for permission. Opt the session into capture synchronously,
      // right before startRecording, so nothing (e.g. a re-render running
      // initializeWaveSurfer) can reset it back to "auto" in between. No-op
      // on engines without the API (Chrome/Firefox).
      if (navigator.audioSession) {
        navigator.audioSession.type = "play-and-record";
      }
      // RecordPlugin hardcodes getUserMedia to {audio:{deviceId}}, leaving
      // Safari's voice-processing (echoCancellation/autoGainControl/
      // noiseSuppression, all default-true) engaged — which guts capture
      // amplitude/quality and adds a hiss via the comms audio route. The plugin
      // exposes no constraints hook, so briefly wrap getUserMedia to disable
      // voice-processing for THIS capture, then restore it. (Wrapping instead of
      // presetting plugin.stream keeps the live recording waveform working.)
      const md = navigator.mediaDevices;
      const nativeGUM = md.getUserMedia.bind(md);
      const restoreGUM = () => {
        try {
          delete md.getUserMedia;
        } catch {
          md.getUserMedia = nativeGUM;
        }
      };
      md.getUserMedia = (constraints) => {
        const audio =
          constraints && constraints.audio
            ? {
                ...(typeof constraints.audio === "object" ? constraints.audio : {}),
                // Keep echoCancellation OFF (it's the main trigger for Safari's
                // telephony routing). Turn noiseSuppression ON — AGC boosts quiet
                // passages including room noise, so without NS the background
                // noise comes up; NS cleans it (a real filter on Chrome; on Safari
                // it rides the voice-processing unit). autoGainControl ON to level
                // the mic at the source.
                echoCancellation: false,
                noiseSuppression: true,
                autoGainControl: true,
                // No channelCount constraint — let the device capture natively
                // (Safari DOES do stereo from a stereo-capable device; forcing
                // channelCount:1 was what pinned it to mono). 48k as ideal.
                sampleRate: 48000,
              }
            : constraints?.audio;
        return nativeGUM({ ...constraints, audio });
      };
      recordPluginRef.current
        .startRecording({ deviceId })
        .then(() => {
          setRecording(true);
          setPlaybackView(false);
          // Permission is now granted, so enumerateDevices() exposes real device
          // labels — refresh the picker (replaces the "Microphone N" fallbacks).
          loadAvailableMics();
        })
        .finally(restoreGUM);
    }
  };

  const handlePlayPause = () => {
    // Do NOT change the audio session here — a route switch at play time glitches
    // the first play. The session is already "playback" by this point.
    waveSurferRef.current.playPause();
  };

  const handleSliderChange = (value) => {
    waveSurferRef.current.seekTo(value / duration);
    setCurrentTime(value);
  };

  const handleDiscard = async () => {
    await clearStoredRecording();
    setProgress("00:00");
    setHasNewRecording(false);
    setPlaybackView(false);
    initializeWaveSurfer();
    // Discarding the new take returns to the existing signature (if any) so Play
    // is available again; otherwise we're left in the empty record state.
    if (uploadType === "signature" && objectId) {
      loadSignatureAudio();
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes < 10 ? "0" + minutes : minutes}:${
      seconds < 10 ? "0" + seconds : seconds
    }`;
  };

  const storeRecording = async (blob) => {
    const db = await initAudioRecordingsDB();
    if (uploadType === "conversation") {
      await db.put(AUDIO_RECORDINGS_DB_STORE, blob, "audioBlob");
    } else if (uploadType === "signature") {
      await db.put(AUDIO_RECORDINGS_DB_STORE, blob, "signatureBlob");
    }
  };

  const loadStoredAudio = useCallback(async () => {
    const db = await initAudioRecordingsDB();
    const blob = await db.get(AUDIO_RECORDINGS_DB_STORE, "audioBlob");
    initializeWaveSurfer();
    if (blob) {
      const recordedUrl = URL.createObjectURL(blob);
      setPlaybackView(true);
      loadWaveSurferWithAudio(recordedUrl);
    }
  }, []);

  const loadSignatureAudio = useCallback(async () => {
    initializeWaveSurfer();
    let signed_url = undefined;
    try {
      if (objectId !== undefined) {
        signed_url = await getSignatureUrl(objectId);
      }
    } catch (error) {
      console.error("Error loading signature audio:", error);
      toast({
        title: "Error",
        description: `Error loading signature audio: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
        containerStyle: { marginTop: "20px" },
      });
    }
    if (signed_url) {
      setIsLoadingSignature(true);
      // fetch the signed url
      // catch any errors
      try {
        const response = await fetch(signed_url).finally(() =>
          setIsLoadingSignature(false)
        );
        const blob = await response.blob();
        const recordedUrl = URL.createObjectURL(blob);
        setPlaybackView(true);
        loadWaveSurferWithAudio(recordedUrl);
        onLoadingChange?.(isLoadingSignature);
      } catch (error) {
        console.error("Error loading signature audio:", error);
        toast({
          title: "Error",
          description: `Error loading signature audio: ${error.message}`,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
          containerStyle: { marginTop: "20px" },
        });
        setPlaybackView(false);
      }
    } else {
      setPlaybackView(false);
      onLoadingChange?.(isLoadingSignature);
    }
  }, [objectId, toast, onLoadingChange, isLoadingSignature]);

  const clearStoredRecording = async () => {
    const db = await initAudioRecordingsDB();
    await db.delete(
      AUDIO_RECORDINGS_DB_STORE,
      uploadType === "signature" ? "signatureBlob" : AUDIO_RECORDINGS_DB_AUDIO_BLOB
    );
  };

  // called when the user clicks the Upload button (saves the new recording)
  const onUploadAudio = async () => {
    setIsUploading(true);
    try {
      await uploadAudio(
        uploadType,
        objectId,
        onUploadComplete,
        setUploadProgress,
        toast
      );
      setHasNewRecording(false);
    } finally {
      setIsUploading(false);
    }
  };

  // A clip (existing signature or a new recording) is loaded and playable.
  const hasAudio = playbackView;
  const isPlaying = waveSurferRef.current?.isPlaying();

  return (
    <VStack spacing={4} align="stretch" position="relative">
      {/* Mic picker — available whenever a new recording can be started */}
      {!initializing && !recording && (
        <Select
          placeholder="Microphone"
          value={selectedMic}
          onChange={(e) => setSelectedMic(e.target.value)}
        >
          {availableMics.map((device, index) => (
            <option
              key={device.deviceId || `mic-${index}`}
              value={device.deviceId}
            >
              {device.label || `Microphone ${index + 1}`}
            </option>
          ))}
        </Select>
      )}

      {recording && (
        <Text textAlign="center" color="red.500" fontVariantNumeric="tabular-nums">
          ● {progress}
        </Text>
      )}

      <Box
        id="mic"
        ref={waveformRef}
        w="100%"
        h="100px"
        border="1px solid #ddd"
      />

      {initializing && (
        <Center py={4}>
          <Spinner size="md" color="blue.500" />
        </Center>
      )}

      {/* Scrubber — only when a clip is loaded */}
      {!initializing && hasAudio && (
        <HStack w="100%">
          <Text fontSize="sm" color="gray.500" minW="36px">
            {formatTime(currentTime)}
          </Text>
          <Slider
            value={currentTime}
            min={0}
            max={duration}
            step={0.01}
            onChange={handleSliderChange}
            flex="1"
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
          <Text fontSize="sm" color="gray.500" minW="36px" textAlign="right">
            {formatTime(duration)}
          </Text>
        </HStack>
      )}

      {/* Three persistent controls: Play · Record/Discard · Upload */}
      {!initializing && (
        <HStack spacing={3}>
          <Button
            flex="1"
            colorScheme="blue"
            variant={hasAudio && !hasNewRecording ? "solid" : "outline"}
            leftIcon={isPlaying ? <HiOutlinePause /> : <HiOutlinePlay />}
            onClick={handlePlayPause}
            isDisabled={recording || !hasAudio || !audioReady}
            isLoading={hasAudio && !audioReady && !recording}
            loadingText="Loading"
          >
            {isPlaying ? "Pause" : "Play"}
          </Button>

          {hasNewRecording && !recording ? (
            <Button
              flex="1"
              colorScheme="red"
              variant="outline"
              leftIcon={<HiOutlineTrash />}
              onClick={handleDiscard}
            >
              Discard
            </Button>
          ) : (
            <Button
              flex="1"
              colorScheme={recording ? "red" : "blue"}
              variant={recording || !hasAudio ? "solid" : "outline"}
              leftIcon={recording ? <HiOutlineStop /> : <AiOutlineAudio />}
              onClick={handleStartStopRecording}
            >
              {recording ? "Stop" : "Record"}
            </Button>
          )}

          <Button
            flex="1"
            colorScheme="green"
            variant={hasNewRecording ? "solid" : "outline"}
            leftIcon={<HiOutlineUpload />}
            onClick={onUploadAudio}
            isDisabled={
              !hasNewRecording || recording || !audioReady || disableCreateButton
            }
          >
            Upload
          </Button>
        </HStack>
      )}

      <Box id="recordings" mt={4} />

      {/* Progress Modal */}
      <Modal isOpen={isUploading} onClose={() => setIsUploading(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Uploading Audio</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Progress value={uploadProgress} colorScheme="green" size="lg" />
            <Text mt={2}>{uploadProgress}%</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setIsUploading(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* {isLoadingSignature && (
        <Center
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="rgba(255, 255, 255, 0.8)"
          zIndex="overlay"
        >
          <VStack>
            <Spinner size="xl" />
            <Text>Loading signature audio...</Text>
          </VStack>
        </Center>
      )} */}
    </VStack>
  );
};

export default AudioRecorder;
